import { useEffect, useRef, useState, type ReactNode } from "react";
import { ConfigProvider, DatePicker, Modal, TimePicker } from "antd";
import { CalendarOutlined, SyncOutlined } from "@ant-design/icons";
import dayjs, { type Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../hooks/useViewport";
import {
  GhostButton,
  ModalFooter,
  ModalHeader,
  PrimaryButton,
  posModalStyles as modalStyles,
} from "../../components/modalPrimitives";
import {
  DISPLAY_DATE,
  ISO_DATE,
  WEEKDAYS_MONDAY_FIRST,
  defaultLunchName,
  latestLunchDate,
  type LunchDraft,
  type LunchScheduleMode,
} from "../lunchDraft";

type LunchSchedule = Pick<
  LunchDraft,
  "name" | "mode" | "dateFrom" | "dateTo" | "weekdays" | "timeFrom" | "timeTo"
>;

interface LunchScheduleModalProps {
  open: boolean;
  schedule: LunchSchedule;
  onClose: () => void;
  onApply: (schedule: LunchSchedule) => void;
}

// Акцентът на „Маркиране" за antd календарите (pos-accent)
const POS_ACCENT = "#C97C2C";
const TIME_FORMAT = "HH:mm";

const FieldLabel = ({ children }: { children: ReactNode }) => (
  <span className="font-pos-mono text-[11px] font-semibold uppercase tracking-[.08em] text-pos-dim">
    {children}
  </span>
);

const pickerClass = "w-full !h-11 !rounded-[10px]";

// Минали дни и дни след днес + 2 години не се избират
const isOutOfRange = (date: Dayjs) =>
  date.isBefore(dayjs(), "day") || date.format(ISO_DATE) > latestLunchDate();

// „до" предлага :04, :09 … :59 — менюто е до края на минутата, а следващото
// започва в :00/:05 … без застъпване (13:00 → „до 12:59")
const TO_MINUTES_HIDDEN = Array.from({ length: 60 }, (_, minute) => minute).filter(
  (minute) => minute % 5 !== 4,
);
const toMinutesDisabled = () => ({ disabledMinutes: () => TO_MINUTES_HIDDEN });

/** „Кога е активно": име, една дата или повтарящо се (дни + период), часове. */
export const LunchScheduleModal = ({ open, schedule, onClose, onApply }: LunchScheduleModalProps) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow } = useViewport();
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={isMobile ? undefined : 560}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <ModalHeader
        icon={<CalendarOutlined />}
        title={t("lunchMenu.scheduleTitle")}
        subtitle={t("lunchMenu.scheduleSubtitle")}
        onClose={onClose}
        isNarrow={isNarrow}
      />
      <ScheduleForm schedule={schedule} onClose={onClose} onApply={onApply} />
    </Modal>
  );
};

const ScheduleForm = ({
  schedule,
  onClose,
  onApply,
}: Omit<LunchScheduleModalProps, "open">) => {
  const { t } = useTranslation();
  const { isNarrow, isDesktop } = useViewport();
  const [value, setValue] = useState<LunchSchedule>(schedule);
  const update = (patch: Partial<LunchSchedule>) => setValue((prev) => ({ ...prev, ...patch }));

  // Enter = „Готово" (Esc затваря сам antd Modal-ът = „Отказ"). В полетата на
  // датите/часовете Enter е на самото поле — там не пипаме; в името — „Готово".
  const valueRef = useRef(value);
  valueRef.current = value;
  useEffect(() => {
    if (!isDesktop) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Enter" || event.defaultPrevented) return;
      const target = event.target as HTMLElement | null;
      if (target?.tagName === "INPUT" && target.dataset.lunchName !== "true") return;
      if (target?.tagName === "BUTTON") return;
      event.preventDefault();
      onApply(valueRef.current);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isDesktop, onApply]);

  // Автоматичното име („Обяд · сряда") следва датата, докато човек не го смени
  const nameIsAuto = value.name === defaultLunchName(t, value.dateFrom);
  const setSingleDate = (date: Dayjs | null) => {
    if (!date) return;
    const iso = date.format(ISO_DATE);
    update({ dateFrom: iso, dateTo: iso, ...(nameIsAuto ? { name: defaultLunchName(t, iso) } : {}) });
  };
  const setMode = (mode: LunchScheduleMode) =>
    update(
      mode === "single"
        ? { mode, dateTo: value.dateFrom }
        : { mode, dateTo: null, weekdays: value.weekdays.length ? value.weekdays : [dayjs(value.dateFrom).day()] },
    );
  const toggleWeekday = (day: number) =>
    update({
      weekdays: value.weekdays.includes(day)
        ? value.weekdays.filter((weekday) => weekday !== day)
        : [...value.weekdays, day],
    });

  const segment = (mode: LunchScheduleMode, icon: ReactNode, label: string) => (
    <button
      type="button"
      onClick={() => setMode(mode)}
      aria-pressed={value.mode === mode}
      className={`inline-flex flex-1 cursor-pointer items-center justify-center gap-2 border-0 text-sm font-semibold transition-colors ${
        value.mode === mode ? "bg-pos-marked text-pos-accent-ink" : "bg-transparent text-pos-soft hover:bg-pos-panel"
      }`}
    >
      {icon}
      {label}
    </button>
  );

  const quickDate = (label: string, date: Dayjs) => (
    <button
      type="button"
      onClick={() => setSingleDate(date)}
      className="h-11 shrink-0 cursor-pointer rounded-[10px] border border-pos-ctl bg-pos-card px-3.5 text-sm font-semibold text-pos-ink2 transition-colors hover:bg-pos-panel"
    >
      {label}
    </button>
  );

  return (
    <ConfigProvider theme={{ token: { colorPrimary: POS_ACCENT } }}>
      <div className={`flex flex-col gap-[18px] ${isNarrow ? "px-4 py-4" : "px-[22px] py-5"}`}>
        <label className="flex flex-col gap-2">
          <FieldLabel>{t("lunchMenu.name")}</FieldLabel>
          <input
            value={value.name}
            onChange={(event) => update({ name: event.target.value })}
            data-lunch-name="true"
            maxLength={255}
            className="h-11 rounded-[10px] border border-pos-ctl bg-pos-card px-3 text-base text-pos-ink outline-none focus:border-pos-accent sm:text-[15px]"
          />
        </label>

        <div className="flex h-11 overflow-hidden rounded-[11px] border border-pos-ctl bg-pos-card">
          {segment("single", <CalendarOutlined />, t("lunchMenu.modeSingle"))}
          <span aria-hidden className="w-px bg-pos-ctl" />
          {segment("recurring", <SyncOutlined />, t("lunchMenu.modeRecurring"))}
        </div>

        {value.mode === "single" ? (
          <div className="flex flex-col gap-2">
            <FieldLabel>{t("lunchMenu.date")}</FieldLabel>
            <div className="flex gap-2">
              <DatePicker
                value={dayjs(value.dateFrom)}
                onChange={setSingleDate}
                format={DISPLAY_DATE}
                allowClear={false}
                disabledDate={isOutOfRange}
                className={pickerClass}
              />
              {quickDate(t("lunchMenu.today"), dayjs())}
              {quickDate(t("lunchMenu.tomorrow"), dayjs().add(1, "day"))}
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-2">
              <FieldLabel>{t("lunchMenu.weekdays")}</FieldLabel>
              <div className="grid grid-cols-7 gap-1.5">
                {WEEKDAYS_MONDAY_FIRST.map((day) => {
                  const active = value.weekdays.includes(day);
                  return (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleWeekday(day)}
                      aria-pressed={active}
                      className={`h-11 cursor-pointer rounded-[10px] border text-sm font-semibold transition-colors ${
                        active
                          ? "border-pos-accent bg-pos-accent text-white"
                          : "border-pos-ctl bg-pos-card text-pos-soft hover:border-pos-accent/40"
                      }`}
                    >
                      {t(`lunchMenu.dayButton${day}`)}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <FieldLabel>{t("lunchMenu.period")}</FieldLabel>
              <div className="grid grid-cols-2 gap-2">
                <DatePicker
                  value={dayjs(value.dateFrom)}
                  onChange={(date) => date && update({ dateFrom: date.format(ISO_DATE) })}
                  format={DISPLAY_DATE}
                  allowClear={false}
                  disabledDate={isOutOfRange}
                  prefix={<span className="text-pos-soft2">{t("lunchMenu.periodFrom")}</span>}
                  className={pickerClass}
                />
                <DatePicker
                  value={value.dateTo ? dayjs(value.dateTo) : null}
                  onChange={(date) => update({ dateTo: date ? date.format(ISO_DATE) : null })}
                  format={DISPLAY_DATE}
                  placeholder={t("lunchMenu.noEndDate")}
                  disabledDate={isOutOfRange}
                  prefix={<span className="text-pos-soft2">{t("lunchMenu.periodTo")}</span>}
                  className={pickerClass}
                />
              </div>
            </div>
          </>
        )}

        <div className="flex flex-col gap-2">
          <FieldLabel>{t("lunchMenu.hours")}</FieldLabel>
          <div className="flex items-center gap-2">
            <TimePicker
              value={dayjs(value.timeFrom, TIME_FORMAT)}
              onChange={(time) => time && update({ timeFrom: time.format(TIME_FORMAT) })}
              // Избраното в отвореното меню се пази веднага — „Готово" без
              // затваряне на менюто иначе губеше новия час
              onCalendarChange={(time) =>
                time && !Array.isArray(time) && update({ timeFrom: time.format(TIME_FORMAT) })
              }
              format={TIME_FORMAT}
              minuteStep={5}
              allowClear={false}
              needConfirm={false}
              className={pickerClass}
            />
            <span className="text-pos-muted">–</span>
            <TimePicker
              value={dayjs(value.timeTo, TIME_FORMAT)}
              onChange={(time) => time && update({ timeTo: time.format(TIME_FORMAT) })}
              onCalendarChange={(time) =>
                time && !Array.isArray(time) && update({ timeTo: time.format(TIME_FORMAT) })
              }
              format={TIME_FORMAT}
              disabledTime={toMinutesDisabled}
              hideDisabledOptions
              allowClear={false}
              needConfirm={false}
              className={pickerClass}
            />
          </div>
        </div>
      </div>
      <ModalFooter isNarrow={isNarrow}>
        <GhostButton
          label={t("common.cancel")}
          onClick={onClose}
          shortcut={isDesktop ? "Esc" : undefined}
        />
        <PrimaryButton
          label={t("lunchMenu.done")}
          onClick={() => onApply(value)}
          shortcut={isDesktop ? "⏎" : undefined}
        />
      </ModalFooter>
    </ConfigProvider>
  );
};
